import React, { useState, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import './RestaurantDetail.css';
import './RestaurantDetailPremium.css';

import logo from '../../assets/waseeny-logo.png';
import { User, ClipboardList, MapPin, LogOut, Star, Clock, Truck, ShoppingBag, ShoppingCart, MoreVertical, Check, Lock, AlertCircle, Zap, ChevronLeft, ChevronRight } from 'lucide-react';
import defaultItemImage from '../../assets/default-item-image.png';
import useRestaurantDetail from "../../Hook/useRestaurantDetail";
import useCart from "../../Hook/useCart";
import { useTranslate } from "../../Hook/useTranslate";
import useDynamicTranslation from "../../Hook/useDynamicTranslation";
import { getAbsoluteImageUrl } from "../../utils/imageUrl";
import { getDeliveryInfo } from "../../utils/deliveryUtils";
import { getCurrencyFromPhone } from "../../utils/phone";
import { formatMoney } from "../../utils/formatMoney";
import { RestaurantDetailSkeleton } from "../Common/PageSkeleton";
import { getSessionToken } from "../../utils/authSession";
import FormBanner from "../Common/FormBanner";
import AllergenLine from '../Common/AllergenLine';
import NotificationBell from '../Common/NotificationBell';

const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', margin: '0 auto', transform: 'translateX(-1px)' }}>
    <polyline points="15 18 9 12 15 6"></polyline>
  </svg>
);

const RestaurantDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const t = useTranslate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('order');
  const [headerSearchActive, setHeaderSearchActive] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(localStorage.getItem('userLocation') || 'Other');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { id } = useParams();
  const { restaurant, menu, loading, error } = useRestaurantDetail(id);
  const { cart, loading: cartLoading, addToCart, updateQuantity, removeItem } = useCart();
  const [addingItemId, setAddingItemId] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUpsell, setShowUpsell] = useState(true);
  const upsellListRef = useRef(null);

  const scrollUpsell = (direction) => {
    if (upsellListRef.current) {
      const scrollAmount = direction === 'left' ? -220 : 220;
      upsellListRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const currencySymbol = React.useMemo(() => getCurrencyFromPhone(restaurant?.contactNumber), [restaurant]);

  // User location from localStorage with fallback (Bochum, Germany default for demo)
  const userLocation = (() => {
    try {
      const stored = localStorage.getItem('userFullAddress');
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          lat: parsed.coordinates?.[1] || 51.4994,
          lng: parsed.coordinates?.[0] || 7.0833
        };
      }
    } catch (e) { }
    return { lat: 51.4994, lng: 7.0833 }; // Default fallback
  })();

  const restWithCoords = restaurant ? {
    ...restaurant,
    lat: restaurant.location?.coordinates?.[1] || restaurant.lat || restaurant.latitude || 51.4994,
    lng: restaurant.location?.coordinates?.[0] || restaurant.lng || restaurant.longitude || 7.0833
  } : null;

  const deliveryInfo = restWithCoords ? getDeliveryInfo(restWithCoords, userLocation?.lat, userLocation?.lng, null, currencySymbol) : null;

  // Customization Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [pendingItem, setPendingItem] = useState(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [formBanner, setFormBanner] = useState({ type: '', text: '' });

  const rawMenuItems = Array.isArray(menu)
    ? menu
    : Object.values(menu || {}).flatMap(cat => cat.items || (Array.isArray(cat) ? cat : []));

  // Filter to ensure unique items by _id
  const menuItems = Array.from(new Map(rawMenuItems.map(item => [item._id, item])).values());

  const extractText = (data) => {
    if (!data) return "";
    if (typeof data === "object") {
      return data.en || data.En || Object.values(data)[0] || "";
    }
    return String(data);
  };

  const allTexts = [
    extractText(restaurant?.name),
    ...menuItems.map(item => extractText(item.name)),
    ...menuItems.map(item => extractText(item.description)),
    ...menuItems.flatMap(item => (item.addOns || []).map(a => extractText(a.name))),
    ...menuItems.flatMap(item => (item.variations || []).map(v => extractText(v.name)))
  ].filter(Boolean);

  const { getText } = useDynamicTranslation(allTexts);

  const handleAddClick = (item) => {
    if (item.available === false) return;
    if (item.variations?.length > 0 || item.addOns?.length > 0) {
      setSelectedProduct(item);
      setSelectedVariation(null);
      setSelectedAddOns([]);
      setIsModalOpen(true);
    } else {
      handleAddToCart(item, null, []);
    }
  };

  const handleAddToCart = async (menuItem, variation = null, addOns = [], clearCart = false) => {
    if (addingItemId === menuItem._id) return;
    try {
      setAddingItemId(menuItem._id);
      const addOnsIds = addOns.map(a => a._id);
      await addToCart(id, menuItem._id, 1, variation?._id, addOnsIds, menuItem, clearCart);
      setIsModalOpen(false);
      setShowConflictModal(false);
      setPendingItem(null);
      setShowSuccessPopup(true);
      setTimeout(() => setShowSuccessPopup(false), 3000);
    } catch (err) {
      console.error('Failed to add to cart:', err);
      if (err.response?.status === 409) {
        setPendingItem({ menuItem, variation, addOns });
        setShowConflictModal(true);
      } else {
        setFormBanner({ type: 'error', text: t("failedToAdd") });
      }
    } finally {
      setAddingItemId(null);
    }
  };

  const getItemCartEntries = (menuItemId) => {
    if (!cart?.items || !Array.isArray(cart.items)) return [];
    return cart.items.filter(it => {
      const pId = it.productId?._id || it.productId || it._id || it.product?._id;
      return pId === menuItemId || String(pId) === String(menuItemId);
    });
  };

  const getItemCartQuantity = (menuItemId) => {
    const entries = getItemCartEntries(menuItemId);
    return entries.reduce((sum, it) => sum + (it.quantity || 1), 0);
  };

  const handleIncrementItem = async (item) => {
    if (item.available === false || addingItemId === item._id) return;
    const entries = getItemCartEntries(item._id);
    if ((item.variations?.length > 0 || item.addOns?.length > 0) && entries.length === 0) {
      handleAddClick(item);
      return;
    }
    if (entries.length > 0) {
      try {
        setAddingItemId(item._id);
        const lastEntry = entries[entries.length - 1];
        const entryId = lastEntry._id || lastEntry.productId;
        await updateQuantity(entryId, (lastEntry.quantity || 1) + 1);
      } catch (err) {
        console.error('Failed to update quantity:', err);
      } finally {
        setAddingItemId(null);
      }
    } else {
      handleAddToCart(item, null, []);
    }
  };

  const handleDecrementItem = async (item) => {
    if (addingItemId === item._id) return;
    const entries = getItemCartEntries(item._id);
    if (entries.length === 0) return;
    try {
      setAddingItemId(item._id);
      const lastEntry = entries[entries.length - 1];
      const entryId = lastEntry._id || lastEntry.productId;
      const currentQty = lastEntry.quantity || 1;
      if (currentQty > 1) {
        await updateQuantity(entryId, currentQty - 1);
      } else {
        await removeItem(entryId);
      }
    } catch (err) {
      console.error('Failed to decrement quantity:', err);
    } finally {
      setAddingItemId(null);
    }
  };

  const getAddOnKey = (a) => a?._id?.toString() || a?.id?.toString() || (typeof a?.name === 'object' ? (a?.name?.en || Object.values(a?.name)[0] || '') : a?.name) || String(a);

  const toggleAddOn = (addOn) => {
    const key = getAddOnKey(addOn);
    setSelectedAddOns(prev => {
      const exists = prev.some(a => getAddOnKey(a) === key);
      if (exists) {
        return prev.filter(a => getAddOnKey(a) !== key);
      } else {
        return [...prev, addOn];
      }
    });
  };

  const calculateTotalPrice = () => {
    if (!selectedProduct) return 0;
    let total = selectedProduct.basePrice;
    if (selectedVariation) total += selectedVariation.price;
    selectedAddOns.forEach(a => total += a.price);
    return total;
  };

  const handleViewCart = () => {
    const token = getSessionToken();
    if (!token) {
      setShowAuthModal(true);
      return;
    }
    navigate('/checkout', { state: { cart: cart.items } });
  };

  const handleLocationChange = () => {
    navigate('/home');
  };

  const handleHeaderSearch = (e) => {
    if (e.key === 'Enter') {
      navigate('/search', { state: { searchQuery: e.target.value } });
    }
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const handleCartClick = () => {
    if (cart.items && cart.items.length > 0) {
      const token = getSessionToken();
      if (!token) {
        setShowAuthModal(true);
        return;
      }
      navigate('/checkout', { state: { cart: cart.items } });
    } else {
      setFormBanner({ type: 'info', text: t("cartEmpty") });
    }
  };

  if (loading) return <RestaurantDetailSkeleton />;
  if (error) return <h2>{t("error")}: {error}</h2>;

  return (
    <div 
      className="restaurant-detail-container" 
      style={{ paddingBottom: cart.items?.length > 0 ? (showUpsell ? '340px' : '180px') : '60px' }}
    >
      <header className="restaurant-header">
        <div className="header-left-detail">
          <button onClick={() => navigate(-1)} className="back-btn-detail" aria-label="Go back">
            <BackIcon />
          </button>
          <img src={logo} alt="Waseeny" className="logo-image-detail" onClick={() => navigate('/')} />
          <div className="header-location-pill" onClick={handleLocationChange} title="Change Delivery Location">
            <MapPin size={16} color="#C5192D" />
            <span className="location-name">{selectedLocation || "Select Location"}</span>
            <svg width="10" height="10" viewBox="0 0 14 14" fill="none">
              <path d="M3.5 5.5L7 9L10.5 5.5" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>

        <nav className="header-nav-detail" aria-label="Restaurant navigation">
          <div className="header-search-v2">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="8" cy="8" r="6" stroke="rgba(0,0,0,0.4)" strokeWidth="1.5" />
              <path d="M12.5 12.5L16 16" stroke="rgba(0,0,0,0.4)" strokeWidth="1.5" />
            </svg>
            <input
              type="text"
              placeholder={t("searchFoodPlaceholder") || "Search for food, dishes..."}
              onKeyPress={handleHeaderSearch}
            />
          </div>

          <NotificationBell />

          <button
            type="button"
            className="nav-icon-circle-btn"
            aria-label="Sign in"
            onClick={handleProfileClick}
            title={t("signIn")}
          >
            <User size={18} color="#1e293b" />
          </button>
          
          <button
            type="button"
            className="nav-icon-circle-btn cart-btn-detail"
            aria-label="Cart"
            onClick={handleCartClick}
            title={t("viewCart")}
          >
            <ShoppingCart size={18} color="#1e293b" strokeWidth={2.2} />
            {cart.items && cart.items.length > 0 && (
              <span className="cart-badge-detail">{cart.items.length}</span>
            )}
          </button>

          <div className="nav-dropdown-container" style={{ position: 'relative' }}>
            <button
              type="button"
              className="nav-icon-circle-btn"
              aria-label="Menu"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <MoreVertical size={18} color="#1e293b" strokeWidth={2} />
            </button>
            {dropdownOpen && (
              <div className="nav-dropdown-menu">
                <div onClick={() => { setDropdownOpen(false); navigate('/profile'); }}>
                  <User size={16} /> My Profile
                </div>
                <div onClick={() => { setDropdownOpen(false); navigate('/order-tracking'); }}>
                  <ClipboardList size={16} /> Active Orders
                </div>
                <div onClick={() => { setDropdownOpen(false); navigate('/addresses'); }}>
                  <MapPin size={16} /> My Addresses
                </div>
                <div className="logout-btn" onClick={async () => {
                  localStorage.clear();
                  window.location.href = '/home';
                }}>
                  <LogOut size={16} /> Log out
                </div>
              </div>
            )}
          </div>
        </nav>
      </header>

      <div className="breadcrumb-wrapper">
        <div className="breadcrumb">
          <span onClick={() => navigate('/home')} className="breadcrumb-link">{t("home") || "Home"}</span>
          <span className="breadcrumb-sep">/</span>
          <span onClick={() => navigate('/home')} className="breadcrumb-link">{restaurant?.city || (selectedLocation && selectedLocation !== 'Other' ? selectedLocation : 'Restaurants')}</span>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">{getText(restaurant?.name) || 'Restaurant'}</span>
        </div>
      </div>

      <div className="restaurant-content">
        <FormBanner
          message={formBanner.text}
          type={formBanner.type || 'error'}
          onDismiss={() => setFormBanner({ type: '', text: '' })}
        />
        <div className="restaurant-hero">
          <div className="restaurant-title-row">
            <h1 className="restaurant-name-title">{getText(restaurant?.name)}</h1>
            <div className="restaurant-rating-chip">
              <Star size={14} fill="white" />
              <span>{restaurant?.rating?.average?.toFixed(1) || '0.0'}</span>
            </div>
            <span className="restaurant-reviews-count">
              ({restaurant?.rating?.count || 0}+ Reviews)
            </span>
          </div>

          {(restaurant?.address || restaurant?.area || restaurant?.city) && (
            <div className="restaurant-address-row">
              <MapPin size={16} color="#C5192D" />
              <span>
                {[
                  getText(restaurant?.address) || restaurant?.address,
                  restaurant?.area,
                  restaurant?.city
                ].filter(Boolean).join(', ')}
              </span>
            </div>
          )}
          
          <div className="restaurant-info-card">
            {/* 4-Chip Stats Grid */}
            <div className="rd-delivery-info-row">
              <div className="rd-info-chip">
                <span className="rd-info-icon"><Clock size={16} color="#64748b" /></span>
                <div className="rd-info-text-wrapper">
                  <span className="rd-info-label">{t("deliveryTime") || "DELIVERY TIME"}</span>
                  <span className="rd-info-value">{deliveryInfo ? deliveryInfo.timeLabel : "25–35 mins"}</span>
                </div>
              </div>

              <div className="rd-info-chip">
                <span className="rd-info-icon"><MapPin size={16} color="#64748b" /></span>
                <div className="rd-info-text-wrapper">
                  <span className="rd-info-label">{t("distance") || "DISTANCE"}</span>
                  <span className="rd-info-value">{deliveryInfo ? deliveryInfo.distLabel : "Nearby"}</span>
                </div>
              </div>

              <div className="rd-info-chip">
                <span className="rd-info-icon"><Truck size={16} color="#64748b" /></span>
                <div className="rd-info-text-wrapper">
                  <span className="rd-info-label">{t("deliveryFee") || "DELIVERY FEE"}</span>
                  <span className={`rd-info-value ${deliveryInfo?.deliveryFee === 'Free Delivery' ? 'free' : 'paid'}`}>
                    {deliveryInfo ? (deliveryInfo.deliveryFee === 'Free Delivery' ? (t('freeDelivery') || 'Free Delivery') : deliveryInfo.deliveryFee.replace(' delivery', '')) : "Free Delivery"}
                  </span>
                </div>
              </div>

              <div className="rd-info-chip">
                <span className="rd-info-icon"><ShoppingBag size={16} color="#64748b" /></span>
                <div className="rd-info-text-wrapper">
                  <span className="rd-info-label">{t("minOrder") || "MIN. ORDER"}</span>
                  <span className="rd-info-value">
                    {formatMoney(restaurant?.minimumOrder || restaurant?.minOrder || 15, { symbol: currencySymbol })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="restaurant-main">
          <div className="menu-section">
            <div className="search-section">
              <div className="search-dishes-wrapper">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="search-dish-icon">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
                <input
                  type="text"
                  className="search-dishes"
                  placeholder={t("searchDishesPlaceholder") || "Search for dishes in this menu..."}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button className="clear-search-btn" onClick={() => setSearchTerm('')}>✕</button>
                )}
              </div>
            </div>

            <div className="menu-items-section">
              <h2 className="section-heading">{searchTerm ? `Search results for "${searchTerm}"` : (t("popular") || "Popular Dishes")}</h2>
              <div className="menu-items-list">
                {menuItems
                  .filter(item => getText(item.name).toLowerCase().includes(searchTerm.toLowerCase()))
                  .map((item) => (
                    <div key={item._id} className={`menu-item-card${item.available === false ? ' menu-item-unavailable' : ''}`}>
                      <div className="menu-item-info">
                        <h3 className="menu-item-name">{getText(item.name)}</h3>
                        {item.available === false && (
                          <span className="menu-item-unavailable-label">Unavailable</span>
                        )}
                        <p className="menu-item-price">{formatMoney(item.basePrice, { symbol: currencySymbol })}{item.unit ? ` / ${item.unit}` : ''}</p>
                        {item.description && <p className="menu-item-description">{getText(item.description)}</p>}
                        <AllergenLine allergens={item.allergens || []} note={item.allergenNote || ''} />
                      </div>
                      <div className="menu-item-image-container">
                        <img
                          src={item.image ? getAbsoluteImageUrl(item.image) : defaultItemImage}
                          alt={getText(item.name)}
                          className="menu-item-image"
                          onError={(e) => { e.target.src = defaultItemImage; }}
                        />
                        {(() => {
                          const itemQty = getItemCartQuantity(item._id);
                          if (item.available === false) {
                            return (
                              <button className="add-btn disabled" disabled>
                                Unavailable
                              </button>
                            );
                          }
                          if (itemQty > 0) {
                            return (
                              <div className="qty-control-btn-group">
                                <button
                                  type="button"
                                  className="qty-btn qty-btn-minus"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDecrementItem(item);
                                  }}
                                  disabled={addingItemId === item._id}
                                  aria-label="Decrease quantity"
                                >
                                  −
                                </button>
                                <span className="qty-count-display">
                                  {addingItemId === item._id ? '...' : itemQty}
                                </span>
                                <button
                                  type="button"
                                  className="qty-btn qty-btn-plus"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleIncrementItem(item);
                                  }}
                                  disabled={addingItemId === item._id}
                                  aria-label="Increase quantity"
                                >
                                  +
                                </button>
                              </div>
                            );
                          }
                          return (
                            <button
                              className="add-btn"
                              onClick={() => handleAddClick(item)}
                              disabled={addingItemId === item._id}
                            >
                              {addingItemId === item._id ? "..." : `+ ${t("add") || "Add"}`}
                            </button>
                          );
                        })()}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Customization Modal */}
      {isModalOpen && selectedProduct && (
        <div className="modal-overlay">
          <div className="customization-modal">
            <div className="modal-header">
              <h3>{getText(selectedProduct.name)}</h3>
              <button className="close-btn" onClick={() => setIsModalOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              {selectedProduct.variations?.length > 0 && (
                <div className="custom-section">
                  <h4>Choose Variation</h4>
                  {selectedProduct.variations.map(v => (
                    <label
                      key={v._id || v.name}
                      className="custom-option"
                      onClick={(e) => {
                        e.preventDefault();
                        setSelectedVariation(prev => {
                          const isSame = prev && (prev._id === v._id || (prev.name && (prev.name.en || prev.name) === (v.name?.en || v.name)));
                          return isSame ? null : v;
                        });
                      }}
                    >
                      <input
                        type="radio"
                        name="variation"
                        checked={Boolean(selectedVariation && (selectedVariation._id === v._id || (selectedVariation.name && (selectedVariation.name.en || selectedVariation.name) === (v.name?.en || v.name))))}
                        readOnly
                      />
                      <span className="option-name">{getText(v.name)}</span>
                      <span className="option-price">+{formatMoney(v.price, { symbol: currencySymbol })}</span>
                    </label>
                  ))}
                </div>
              )}

              {selectedProduct.addOns?.length > 0 && (
                <div className="custom-section">
                  <h4>Add-ons / Toppings</h4>
                  {selectedProduct.addOns.map(a => (
                    <label key={a._id} className="custom-option">
                      <input
                        type="checkbox"
                        checked={selectedAddOns.some(addon => getAddOnKey(addon) === getAddOnKey(a))}
                        onChange={() => toggleAddOn(a)}
                      />
                      <span className="option-name">{getText(a.name)}</span>
                      <span className="option-price">+{formatMoney(a.price, { symbol: currencySymbol })}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <div className="total-price-preview">
                Total: {formatMoney(calculateTotalPrice(), { symbol: currencySymbol })}
              </div>
              <button
                className="confirm-add-btn"
                onClick={() => handleAddToCart(selectedProduct, selectedVariation, selectedAddOns)}
                disabled={addingItemId === selectedProduct._id}
              >
                {addingItemId === selectedProduct._id ? t("adding") + "..." : t("add") + " " + t("toCart")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cart Conflict Modal */}
      {showConflictModal && (
        <div className="modal-overlay">
          <div className="conflict-modal">
            <div className="conflict-modal-content">
              <div className="conflict-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', width: '56px', height: '56px', borderRadius: '50%', background: '#fef3c7' }}>
                <AlertCircle size={32} color="#d97706" />
              </div>
              <h3>Items already in cart</h3>
              <p>{t("cartConflictMessage")}</p>
              <div className="conflict-actions">
                <button
                  className="cancel-conflict-btn"
                  onClick={() => setShowConflictModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="confirm-clear-btn"
                  onClick={() => handleAddToCart(pendingItem.menuItem, pendingItem.variation, pendingItem.addOns, true)}
                >
                  Clear Cart & Add
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Toast Notification */}
      {showSuccessPopup && (
        <div className="success-toast">
          <div className="success-toast-content">
            <div className="success-toast-icon-wrapper">
              <Check size={16} strokeWidth={3} color="#ffffff" />
            </div>
            <span className="success-toast-text">{t("itemAdded") || "Item added to cart"}</span>
          </div>
        </div>
      )}

      {cart.items && cart.items.length > 0 && (
        <div className="cart-footer-wrapper">
          {/* Smart Cart Upselling Section */}
          {(() => {
            const upsellingRules = {
              'pizza': ['coke', 'pepsi', 'garlic bread', 'drink', 'beverage', 'cold drink'],
              'burger': ['fries', 'coke', 'pepsi', 'drink', 'beverage'],
              'pasta': ['garlic bread', 'drink'],
              'biryani': ['raita', 'coke', 'pepsi', 'dessert', 'gulab jamun'],
              'sandwich': ['fries', 'drink'],
            };

            const cartItemNames = cart.items.map(i => getText(i.menuItem?.name || i.name).toLowerCase());
            let suggestedTerms = [];

            // 1. Find matching rules
            cartItemNames.forEach(name => {
              Object.keys(upsellingRules).forEach(key => {
                if (name.includes(key)) {
                  suggestedTerms = [...suggestedTerms, ...upsellingRules[key]];
                }
              });
            });

            // 2. Always add a few general categories as fallbacks
            suggestedTerms = [...new Set([...suggestedTerms, 'drink', 'beverage', 'dessert', 'water', 'sweet', 'side'])];

            // 3. Find actual items from the restaurant menu
            let suggestions = menuItems
              .filter(item => {
                const itemName = getText(item.name).toLowerCase();
                const itemCat = (item.categoryName || item.category || "").toLowerCase();

                // Don't suggest items already in cart
                if (cartItemNames.some(cn => cn.includes(itemName) || itemName.includes(cn))) return false;

                // Match by terms OR common upselling categories
                const isMatch = suggestedTerms.some(term => itemName.includes(term) || itemCat.includes(term));
                const isUpsellCat = itemCat.includes('beverage') || itemCat.includes('drink') || itemCat.includes('dessert') || itemCat.includes('side');

                return isMatch || isUpsellCat;
              })
              .sort((a, b) => a.basePrice - b.basePrice) // Show cheaper items firs
              .slice(0, 5);

            // 4. Ultimate Fallback: If still no suggestions, just show some other items from the menu
            if (suggestions.length < 2) {
              const otherItems = menuItems
                .filter(item => !cartItemNames.some(cn => getText(item.name).toLowerCase().includes(cn)))
                .sort((a, b) => a.basePrice - b.basePrice)
                .slice(0, 3);
              suggestions = [...new Set([...suggestions, ...otherItems])].slice(0, 5);
            }

            if (suggestions.length === 0 || !showUpsell) return null;

            const headerTitle = (t("oftenOrderedWith") && t("oftenOrderedWith") !== "oftenOrderedWith")
              ? t("oftenOrderedWith")
              : (t("completeMeal") && t("completeMeal") !== "completeMeal")
                ? t("completeMeal")
                : "People also like";

            return (
              <div className="smart-cart-upsell">
                <div className="upsell-header">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                    <Zap size={14} color="#e31e24" />
                    {headerTitle}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <button 
                      className="upsell-nav-btn" 
                      onClick={(e) => { e.stopPropagation(); scrollUpsell('left'); }}
                      aria-label="Previous suggestions"
                      type="button"
                    >
                      <ChevronLeft size={15} />
                    </button>
                    <button 
                      className="upsell-nav-btn" 
                      onClick={(e) => { e.stopPropagation(); scrollUpsell('right'); }}
                      aria-label="Next suggestions"
                      type="button"
                    >
                      <ChevronRight size={15} />
                    </button>
                    <button 
                      className="upsell-close-btn" 
                      onClick={(e) => { e.stopPropagation(); setShowUpsell(false); }}
                      aria-label="Close recommendations"
                      type="button"
                    >
                      ×
                    </button>
                  </div>
                </div>
                <div className="upsell-list" ref={upsellListRef}>
                  {suggestions.map(item => (
                    <div key={item._id} className="upsell-item" title={getText(item.name)}>
                      <img
                        src={item.image ? getAbsoluteImageUrl(item.image) : defaultItemImage}
                        alt=""
                        className="upsell-img"
                        onError={(e) => { e.target.src = defaultItemImage; }}
                      />
                      <div className="upsell-info">
                        <span className="upsell-name" title={getText(item.name)}>{getText(item.name)}</span>
                        <span className="upsell-price">{formatMoney(item.basePrice, { symbol: currencySymbol })}</span>
                      </div>
                      <button className="upsell-add-btn" onClick={() => handleAddClick(item)}>
                        + {t("add") || "ADD"}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          <div className="cart-footer">
            <div className="cart-footer-content">
              <div className="cart-info">
                <span className="cart-count">{cart.items.length} {t("itemInCart")}</span>
                {cart.total && <span className="cart-total">{formatMoney(cart.total, { symbol: currencySymbol })}</span>}
              </div>
              <button className="view-cart-btn" onClick={handleViewCart}>{t("viewCart")} →</button>
            </div>
          </div>
        </div>
      )}

      {/* Gorgeous Premium Auth Modal */}
      {showAuthModal && (
        <div className="auth-modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.65)', backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 99999, animation: 'fadeIn 0.25s ease'
        }}>
          <div className="auth-modal-box" style={{
            backgroundColor: '#ffffff', borderRadius: '24px', padding: '36px 30px',
            width: '90%', maxWidth: '420px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            textAlign: 'center', transform: 'scale(1)', animation: 'scaleUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
          }}>
            <div style={{
              width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#fee2e2',
              color: '#ed2026', display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 18px auto', boxShadow: '0 8px 16px rgba(237, 32, 38, 0.15)'
            }}>
              <Lock size={28} color="#ed2026" strokeWidth={2.2} />
            </div>
            <h3 style={{ fontSize: '24px', fontWeight: '800', color: '#111111', marginBottom: '10px' }}>
              {t("loginRequired") || "Login Required"}
            </h3>
            <p style={{ fontSize: '15px', color: '#666666', lineHeight: '1.6', marginBottom: '28px' }}>
              {t("loginRequiredSub") || "Please log in to your account to view your cart and place your order."}
            </p>
            <div style={{ display: 'flex', gap: '14px', flexDirection: 'column' }}>
              <button
                onClick={() => navigate('/login', { state: { from: location } })}
                style={{
                  backgroundColor: '#ed2026', color: '#ffffff', border: 'none', borderRadius: '14px',
                  padding: '15px 24px', fontSize: '16px', fontWeight: '700', cursor: 'pointer',
                  boxShadow: '0 8px 20px rgba(237, 32, 38, 0.3)', transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {t("logInNow") || "Log In Now"}
              </button>
              <button
                onClick={() => setShowAuthModal(false)}
                style={{
                  backgroundColor: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '16px',
                  padding: '16px 24px', fontSize: '16px', fontWeight: '700', cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e2e8f0'; e.currentTarget.style.color = '#334155'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; e.currentTarget.style.color = '#64748b'; }}
              >
                {t("cancel") || "Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RestaurantDetail;
